import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import Grid from "@mui/material/Grid";
import { styled } from "@mui/material/styles";
import comm from "csi/comm";
import locale from "csi/locale";
import { ATTR_THUMBNAIL } from "csi/model/image";
import { C8Node } from "csi/typings";
import { PATH_OP_INDEX, pathCreate } from "csi/utility/pathUtility";
import FullScreenDialog from "designSystem/components/dialog/FullScreenDialog";
import { ModalDialogProps } from "designSystem/components/dialog/ModalDialog";
import FlexRow from "designSystem/components/flex/FlexRow";
import { AspectBox, Box, Button, CsiImage, CssGrid, FlexRowCenter, FormControlLabel, GridRow, IconButton, OverflowText, Popover, Switch, Text } from "designSystem/index";
import { black, grey, white } from "designSystem/styles/color";
import { POPOVER_BOTTOM } from "designSystem/styles/popover-positions";
import { HOVER } from "designSystem/theme";
import { IndexFunctionComponent } from "designSystem/types/list-types";
import { scrollToElement } from "designSystem/utilities/scroll-to-element";
import _ from "lodash";
import { observer } from "mobx-react-lite";
import React, { FunctionComponent, KeyboardEventHandler, MouseEventHandler, ReactNode, useEffect, useRef, useState } from "react";
import CanvasNotes from "./CanvasNotes";
import { usePresentationPopupStore } from "./PresentationPopupStore";
import { useEventListener } from "designSystem/hooks";

const PREFIX = "PresentationPopup";

const classes = {
	popoverPaper: `${PREFIX}popoverPaper`,
	slideButton: `${PREFIX}slideButton`,
	slideButtonSelected: `${PREFIX}slideButtonSelected`,
	header: `${PREFIX}header`,
	headerToolbar: `${PREFIX}headerToolbar`,
	fullImage: `${PREFIX}fullImage`,
	fullscreenIcon: `${PREFIX}fullscreenIcon`,
	slidesBar: `${PREFIX}slidesBar`,
	layoutButton: `${PREFIX}layoutButton`,
	layoutButtonSelected: `${PREFIX}layoutButtonSelected`,
	layoutButtonImageBox: `${PREFIX}layoutButtonImageBox`,
	layoutButtonTextBox: `${PREFIX}layoutButtonTextBox`,
	layoutButtonText: `${PREFIX}layoutButtonText`,
	switchSlideBox: `${PREFIX}switchSlideBox`,
	switchSlideBoxLeft: `${PREFIX}switchSlideBoxLeft`,
	switchSlideBoxRight: `${PREFIX}switchSlideBoxRight`,
};

const StyledFullScreenDialog = styled(FullScreenDialog)((
	{
		theme,
	},
) => ({
	[`& .${classes.popoverPaper}`]: {
		borderColor: black[300],
		borderWidth: "0 0 1px 0",
	},

	[`& .${classes.slideButton}`]: {
		borderRadius: 0,
		width: 212,
		paddingBottom: 18,
	},

	[`& .${classes.slideButtonSelected}`]: {
		backgroundColor: black[300],
		[HOVER]: {
			backgroundColor: black[300],
		},
	},

	[`& .${classes.header}`]: {
		backgroundColor: black[50],
		color: grey[400],
		flexShrink: 0,
		flexWrap: "nowrap",
		overflow: "hidden",
		paddingLeft: theme.spacing(4),
		paddingRight: theme.spacing(4),
		transition: "height 200ms",
		width: "100%",
		zIndex: 1,
	},

	[`& .${classes.headerToolbar}`]: {
		alignItems: "center",
		flexShrink: 0,
		justifyContent: "flex-end",
		width: TOOLBAR_WIDTH,
	},

	[`& .${classes.fullImage}`]: {
		"& img": {
			width: "100%",
		},
	},

	[`& .${classes.fullscreenIcon}`]: {
		color: white[50],
		fontSize: "30px !important",
	},

	[`& .${classes.slidesBar}`]: {
		flexShrink: 0,
		overflowY: "scroll",
		overflowX: "hidden",
		width: SLIDE_WIDTH,
	},

	[`& .${classes.switchSlideBox}`]: {
		backgroundColor: "rgba(0,0,0,0.4)",
		borderRadius: 3,
		color: white[50],
		cursor: "pointer",
		padding: theme.spacing(2),
		position: "absolute",
		top: "calc(50% - 24px)",
	},

	[`& .${classes.switchSlideBoxLeft}`]: {
		left: theme.spacing(8),
	},

	[`& .${classes.switchSlideBoxRight}`]: {
		right: theme.spacing(8),
	},
}));

const StyledBox = styled(Box)((
	{
		theme,
	},
) => ({
	[`& .${classes.layoutButton}`]: {
		borderRadius: 3,
		backgroundColor: black[200],
		color: white[50],
		display: "flex",
		height: 116,
		width: 381,
		[HOVER]: {
			backgroundColor: black[500],
		},
	},

	[`& .${classes.layoutButtonSelected}`]: {
		borderRadius: 3,
		backgroundColor: black[400],
		color: white[50],
		display: "flex",
		height: 116,
		width: 381,
		pointerEvents: "none",
	},

	[`& .${classes.layoutButtonImageBox}`]: {
		backgroundColor: black[100],
		display: "inline-block",
		width: 116,
		height: 116,
		borderRadius: "3px 0px 0px 3px",
		overflow: "hidden",
	},

	[`& .${classes.layoutButtonTextBox}`]: {
		display: "inline-block",
		fontSize: 16,
		lineHeight: 18,
		width: 246,
		height: 72,
		marginTop: 12,
		marginLeft: 12,
		marginRight: 12,
		marginBottom: 26,
		textOverflow: "ellipsis",
		textAlign: "left",
	},

	[`& .${classes.layoutButtonText}`]: {
		verticalAlign: "top",
		fontSize: 16,
		lineHeight: "18px",
		width: 246,
		height: 72,
		position: "relative",
		pointerEvents: "none",
		display: "-webkit-box",
		"-webkit-line-clamp": 4,
		"-webkit-box-orient": "vertical",
		overflow: "hidden",
	},
}));

const HEADER_HEIGHT = 48;
const SLIDE_WIDTH = 235;
const TOOLBAR_WIDTH = 300;

export interface PresentationPopupProps extends ModalDialogProps {
}

const PresentationLayouts = observer((props) => {
	const {
		layoutButton,
		layoutButtonImageBox,
		layoutButtonSelected,
		layoutButtonText,
		layoutButtonTextBox,
	} = classes;

	const store = usePresentationPopupStore();
	const {
		allLayouts,
	} = store;

	const makeLayout = (item: C8Node, i: number) => {
		const cn = i == store.layoutIndex ? layoutButtonSelected : layoutButton;
		const src = comm.getNodeFileHref(item.$URL, pathCreate("Pages", PATH_OP_INDEX, "0", "Snapshot"), ATTR_THUMBNAIL);

		return (
			<Grid
				item
				key={"presentation_popup_layout_" + i}
				style={{
					width: 381,
					height: 116,
					marginTop: 12,
					paddingLeft: 16,
				}}
			>
				<Box className={cn} onClick={() => { store.switchLayout(item); store.hideLayouts(); }}>
					<Box className={layoutButtonImageBox}>
						<CsiImage src={src} />
					</Box>
					<Box className={layoutButtonTextBox}>
						<Box className={layoutButtonText}>
							{item.$Name}
						</Box>
					</Box>
				</Box>
			</Grid>
		);
	};

	return (
		<StyledBox
			className="presentationPopupSlideScrollbar"
			style={{
				backgroundColor: black[300],
				maxHeight: `calc(100vh - ${HEADER_HEIGHT + 20}px)`,
				overflow: "hidden scroll",
				paddingBottom: 40,
				paddingLeft: 10,
				paddingRight: 10,
				width: 430,
			}}
		>
			<Grid
				container
				direction="column"
				spacing={5}
			>
				{
					allLayouts.map(makeLayout)
				}
			</Grid>
		</StyledBox>
	);
});

const PresentationPopupSlide: IndexFunctionComponent = observer(({ index }) => {
	const store = usePresentationPopupStore();
	const {
		getSlideDimensions,
		getSlideSplitPct,
		getSlideSrc,
		layoutAspectRatio,
		notesLeft,
		selectedImageIndex,
		switchPage,
	} = store;
	const {
		slideButton,
		slideButtonSelected,
	} = classes;

	const selected = index === selectedImageIndex;
	const ref = useRef(null);

	const splitPct = getSlideSplitPct(index);
	const { totalHeight, thumbWidth, thumbHeight, boxWidth, boxHeight } = getSlideDimensions(index);

	useEffect(() => {
		if (selected) {
			scrollToElement(ref.current);
		}
	}, [selected]);

	return (
		<FlexRow
			className={`${slideButton} ${selected ? slideButtonSelected : ""}`}
			onClick={() => switchPage(index)}
		>
			<div
				ref={ref}
				style={{
					color: white[50],
					paddingTop: 12,
					paddingLeft: 12,
					width: 28,
				}}
			>
				<Text size={12} text={index + 1} />
			</div>
			<div
				style={{
					width: 160,
					marginTop: 12,
				}}
			>
				<AspectBox
					aspectRatio={layoutAspectRatio}
					height={totalHeight}
					innerBoxProps={{
						bgcolor: white[50],
						display: "flex",
						flexDirection: notesLeft ? "row" : "column",
					}}
					maxWidth={160}
				>
					{(splitPct > 0.01) && (
						<CsiImage
							height={thumbHeight}
							src={getSlideSrc(index)}
							style={{
								backgroundColor: white[50],
							}}
							width={thumbWidth}
						/>
					)}
					{(splitPct < .99) && (
						<Box
							style={{
								backgroundColor: white[50],
								height: boxHeight,
								width: boxWidth,
							}}
						/>
					)}
				</AspectBox>
			</div>
		</FlexRow>
	);
});

const PresentationPopupSlides = observer(() => {
	const store = usePresentationPopupStore();
	const { numberOfSlides, showSlides } = store;
	const { slidesBar } = classes;

	if (!showSlides) {
		return null;
	}

	return (
		<div className={`presentationPopupSlideScrollbar ${slidesBar}`}>
			<CssGrid columns={1}>
				{
					_.range(0, numberOfSlides)
						.map((index) => (
							<PresentationPopupSlide index={index} key={index} />
						))
				}
			</CssGrid>
		</div>
	);
});

const PresentationPopupLeft = observer(() => {
	const {
		cache,
		prevImage,
		prevLayout,
		selectedImageIndex,
		showLeftArrow,
	} = usePresentationPopupStore();
	const {
		switchSlideBox,
		switchSlideBoxLeft,
	} = classes;

	const hasPreviousSlide = selectedImageIndex > 0;

	if (!showLeftArrow || (!hasPreviousSlide && !prevLayout)) {
		return null;
	}

	return (
		<FlexRowCenter
			className={`${switchSlideBox} ${switchSlideBoxLeft}`}
			onClick={prevImage}
		>
			<Box display="flex">
				<ArrowBackIcon fontSize="large" style={{ color: white[50] }} />
			</Box>
			{!hasPreviousSlide && prevLayout && (
				<Box pl={2} width={150}>
					<Text
						block
						bold
						lineHeight={16}
						res="PresentationPopupPreviousLayout"
					/>
					<OverflowText lineHeight={16} text={locale.getNodeConfiguredName(prevLayout, cache)} />
				</Box>
			)}
		</FlexRowCenter>
	);
});

const PresentationPopupRight = observer(() => {
	const store = usePresentationPopupStore();
	const {
		cache,
		nextImage,
		nextLayout,
		numberOfImages,
		selectedImageIndex,
		showRightArrow,
	} = store;
	const {
		switchSlideBox,
		switchSlideBoxRight,
	} = classes;

	const hasNextSlide = selectedImageIndex < numberOfImages - 1;

	if (!showRightArrow || (!hasNextSlide && !nextLayout)) {
		return null;
	}

	return (
		<FlexRowCenter
			className={`${switchSlideBox} ${switchSlideBoxRight}`}
			onClick={nextImage}
		>
			{!hasNextSlide && nextLayout && (
				<Box pr={2} width={150}>
					<Text
						block
						bold
						lineHeight={16}
						res="PresentationPopupNextLayout"
					/>
					<OverflowText lineHeight={16} text={locale.getNodeConfiguredName(nextLayout, cache)} />
				</Box>
			)}
			<Box display="flex">
				<ArrowForwardIcon fontSize="large" style={{ color: white[50] }} />
			</Box>
		</FlexRowCenter>
	);
});

interface PresentationPopupHeaderProps {
	/** callback to use when closing popup */
	onClose: VoidFunction;
}

const PresentationPopupHeader: FunctionComponent<PresentationPopupHeaderProps> = observer(({ onClose }) => {
	const ref = useRef(null);
	const store = usePresentationPopupStore();
	const {
		cache,
		handleFullScreenChange,
		hideHeader,
		isFullScreen,
		layout,
		numberOfImages,
		onClickFullscreen,
		selectedImageIndex,
		setIsFullScreen,
		showHeader,
		showLayouts,
		showSlides,
		toggleShowLayouts,
		toggleSlides,
	} = store;

	const {
		fullscreenIcon,
		header,
		headerToolbar,
		popoverPaper,
	} = classes;

	useEventListener("fullscreenchange", handleFullScreenChange);

	return (
		<FlexRowCenter
			className={header}
			height={(isFullScreen && !showHeader) ? 0 : HEADER_HEIGHT}
			onMouseLeave={hideHeader}
			position={isFullScreen ? "absolute" : "static"}
		>
			<Box
				color={grey[800]}
				flexShrink={0}
				width={TOOLBAR_WIDTH}
			>
				<Text
					lineHeight={16}
					size={14}
					text={`${selectedImageIndex + 1} / ${numberOfImages}`}
				/>
			</Box>
			<Box
				centerContent
				color={grey[400]}
				flexGrow={1}
				height={1}
				overflow="hidden"
				{...{ ref: ref }}
			>
				<Button
					design="blend"
					endIcon={(
						<ArrowDropDownIcon
							style={{
								height: 24,
								width: 24,
							}}
						/>
					)}
					hideOverflow
					onClick={toggleShowLayouts}
				>
					<OverflowText
						lineHeight={18}
						size={16}
						text={locale.getNodeConfiguredName(layout, cache)}
					/>
				</Button>
				<Popover
					anchorEl={ref.current}
					classes={{ paper: popoverPaper }}
					container={document.fullscreenElement}
					onClose={toggleShowLayouts}
					open={showLayouts}
					{...POPOVER_BOTTOM}
				>
					<PresentationLayouts />
				</Popover>
			</Box>
			<GridRow
				className={headerToolbar}
				spacing={4}
			>
				<FormControlLabel
					control={<Switch checked={showSlides} onChange={toggleSlides} />}
					labelPlacement="start"
					res="PresentationPopupShowSlides"

				/>
				<IconButton
					design="iconOnlyOnColor"
					onClick={onClickFullscreen}
					size="large"
				>
					{isFullScreen ?
						<FullscreenExitIcon className={fullscreenIcon} />
						:
						<FullscreenIcon className={fullscreenIcon} />
					}
				</IconButton>
				<Button
					design="secondary"
					onClick={onClose}
					res="PresentationPopupEndPresentation"
				/>
			</GridRow>
		</FlexRowCenter>
	);
});

const PresentationPopupContent = observer(() => {
	const store = usePresentationPopupStore();
	const {
		layoutAspectRatio,
		notesLeft,
		selectedImage,
		splitPct,
	} = store;
	const notesBorder = `7px solid ${grey[200]}`;
	let children: ReactNode;

	if (!splitPct) {
		// notes only
		children = (
			<CanvasNotes />
		);
	} else if (splitPct === 1) {
		// image only
		if (selectedImage) {
			children = (
				<CsiImage
					className={classes.fullImage}
					src={selectedImage}
				/>
			);
		}
	} else if (notesLeft) {
		// horizontal image and notes
		children = (
			<>
				<CsiImage
					className={classes.fullImage}
					flexShrink={0}
					src={selectedImage}
					width={splitPct}
				/>
				<Box
					borderLeft={notesBorder}
					growNoOverflow
				>
					<CanvasNotes />
				</Box>
			</>
		);
	} else {
		// vertical image and notes
		children = (
			<>
				<CsiImage
					className={classes.fullImage}
					flexShrink={0}
					height={splitPct}
					src={selectedImage}
				/>
				<Box
					borderTop={notesBorder}
					growNoOverflow
				>
					<CanvasNotes />
				</Box>
			</>
		);
	}

	return (
		<AspectBox
			aspectRatio={layoutAspectRatio}
			centerContent
			innerBoxProps={{
				bgcolor: white[50],
				display: "flex",
				flexDirection: notesLeft ? "row" : "column",
			}}
			maxHeight={1}
			maxWidth={1}
		>
			{children}
		</AspectBox>
	);
});

const PresentationPopupBody = observer(() => {
	const store = usePresentationPopupStore();
	const {
		toggleLeftArrow,
		toggleRightArrow,
	} = store;

	return (
		<Box
			bgcolor={black[50]}
			display="flex"
			growNoOverflow
		>
			<PresentationPopupSlides />
			<Box
				centerContent
				growNoOverflow
				position="relative"
			>
				<PresentationPopupContent />
				<Box
					height={1}
					left={0}
					onMouseEnter={() => toggleLeftArrow(true)}
					onMouseLeave={() => toggleLeftArrow(false)}
					position="absolute"
					width={250}
				>
					<PresentationPopupLeft />
				</Box>
				<Box
					height={1}
					onMouseEnter={() => toggleRightArrow(true)}
					onMouseLeave={() => toggleRightArrow(false)}
					position="absolute"
					right={0}
					width={250}
				>
					<PresentationPopupRight />
				</Box>
			</Box>
		</Box>
	);
});

const PresentationPopup: FunctionComponent<PresentationPopupProps> = observer((props) => {
	const { onClose, ...otherProps } = props;
	const store = usePresentationPopupStore();

	const {
		makeHeaderVisible,
		nextImage,
		prevImage,
		start,
		started,
	} = store;

	const _onKeyDown: KeyboardEventHandler<HTMLDivElement> = ({ key }) => {
		if (key == "ArrowRight") {
			nextImage();
		} else if (key == "ArrowLeft") {
			prevImage();
		} else if (key == "Escape") {
			onClose();
		}
	};

	const _onMouseEnter: MouseEventHandler<HTMLDivElement> = () => {
		if (!started) {
			start();
		}
	};

	const _onMouseMove: MouseEventHandler<HTMLDivElement> = ({ clientY }) => {
		if (clientY < HEADER_HEIGHT) {
			makeHeaderVisible();
		}
	};

	return (
		<StyledFullScreenDialog
			onKeyDown={_onKeyDown}
			onMouseEnter={_onMouseEnter}
			onMouseMove={_onMouseMove}
			{...otherProps}
		>
			{started && (
				<>
					<PresentationPopupHeader
						onClose={onClose}
					/>
					<PresentationPopupBody />
				</>
			)}
		</StyledFullScreenDialog>
	);
});

export default PresentationPopup;
